import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useAsyncResource } from '../hooks/useAsyncResource'
import { foundersAPI } from '../services/api'
import CertifiedBadge from '../components/CertifiedBadge'
import './Page.css'
import './FoundersWall.css'

const PAGE_SIZE = 24

function FoundersWall() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [foundersSummary, setFoundersSummary] = useState({
    founding_count: 0,
    founding_cap: 100,
    program_open: true,
  })
  const [sort, setSort] = useState('most_contributions')
  const [page, setPage] = useState(1)

  useEffect(() => {
    foundersAPI.get({ limit: 1 })
      .then((data) => {
        setFoundersSummary({
          founding_count: Number(data?.founding_count || 0),
          founding_cap: Number(data?.founding_cap || 100),
          program_open: data?.program_open !== false,
        })
      })
      .catch(() => {
        setFoundersSummary({
          founding_count: 0,
          founding_cap: 100,
          program_open: true,
        })
      })
  }, [])

  const offset = (page - 1) * PAGE_SIZE
  const { data: listData, loading } = useAsyncResource(
    (signal) => foundersAPI.get({ sort, limit: PAGE_SIZE, offset, signal }),
    [sort, page]
  )
  const founders = Array.isArray(listData?.founders) ? listData.founders : []
  const total = Number(listData?.total || 0)

  useEffect(() => {
    setPage(1)
  }, [sort])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const progressPercent = Math.min(
    100,
    Math.round((foundersSummary.founding_count / foundersSummary.founding_cap) * 100)
  )
  const handleCreateRepository = () => {
    if (!isAuthenticated) {
      navigate('/', {
        state: {
          showLogin: true,
          redirectTo: '/dashboard',
        },
      })
      return
    }
    navigate('/dashboard', { state: { openNewRepository: true } })
  }

  return (
    <div className="page-container">
      <section className="founders-page-hero">
        <div className="founders-page-hero-backdrop" aria-hidden="true">
          <div className="founders-globe-container">
            <svg className="founders-globe" viewBox="0 0 300 300" preserveAspectRatio="xMidYMid meet">
            <circle cx="150" cy="150" r="110" fill="none" stroke="var(--accent)" strokeWidth="1.5" opacity="0.3" />
            <ellipse cx="150" cy="150" rx="110" ry="55" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22" />
            <ellipse cx="150" cy="120" rx="95" ry="48" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22" />
            <ellipse cx="150" cy="180" rx="95" ry="48" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22" />
            <path d="M 150 40 Q 150 150, 150 260" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22" />
            <path d="M 40 150 Q 150 150, 260 150" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22" />
            <path d="M 75 60 Q 150 150, 225 240" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22" />
            <path d="M 225 60 Q 150 150, 75 240" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22" />
            <circle cx="110" cy="110" r="3" fill="var(--accent)" opacity="0.6" className="globe-node">
              <animate attributeName="opacity" values="0.4;0.7;0.4" dur="2s" repeatCount="indefinite" />
            </circle>
            <circle cx="190" cy="110" r="3" fill="var(--accent)" opacity="0.6" className="globe-node">
              <animate attributeName="opacity" values="0.4;0.7;0.4" dur="2.5s" repeatCount="indefinite" />
            </circle>
            <circle cx="110" cy="190" r="3" fill="var(--accent)" opacity="0.6" className="globe-node">
              <animate attributeName="opacity" values="0.4;0.7;0.4" dur="2.2s" repeatCount="indefinite" />
            </circle>
            <circle cx="190" cy="190" r="3" fill="var(--accent)" opacity="0.6" className="globe-node">
              <animate attributeName="opacity" values="0.4;0.7;0.4" dur="2.8s" repeatCount="indefinite" />
            </circle>
            <circle cx="150" cy="150" r="5" fill="var(--accent)" opacity="0.8" className="globe-node hub">
              <animate attributeName="opacity" values="0.6;0.9;0.6" dur="2s" repeatCount="indefinite" />
              <animate attributeName="r" values="4;6;4" dur="2s" repeatCount="indefinite" />
            </circle>
            <line x1="150" y1="150" x2="110" y2="110" stroke="var(--accent)" strokeWidth="1.5" opacity="0.3" className="connection-line">
              <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2.5s" repeatCount="indefinite" />
            </line>
            <line x1="150" y1="150" x2="190" y2="110" stroke="var(--accent)" strokeWidth="1.5" opacity="0.3" className="connection-line">
              <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2.7s" repeatCount="indefinite" />
            </line>
            <line x1="150" y1="150" x2="110" y2="190" stroke="var(--accent)" strokeWidth="1.5" opacity="0.3" className="connection-line">
              <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2.6s" repeatCount="indefinite" />
            </line>
            <line x1="150" y1="150" x2="190" y2="190" stroke="var(--accent)" strokeWidth="1.5" opacity="0.3" className="connection-line">
              <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2.4s" repeatCount="indefinite" />
            </line>
          </svg>
          </div>
        </div>
        <div className="founders-page-hero-top">
          <h1>Wall of Founders</h1>
          <span className={`founders-program-status ${foundersSummary.program_open ? 'open' : 'closed'}`}>
            {foundersSummary.program_open ? 'Program Open' : 'Program Closed'}
          </span>
        </div>
        <p className="founders-page-description">
          Built by contributors. Earn your place among the first 100 engineers to shape
          the open reverse-engineering community.
        </p>
        <div className="founders-page-progress-header">
          <span>Founding spots filled</span>
          <strong>{foundersSummary.founding_count}/{foundersSummary.founding_cap}</strong>
        </div>
        <div className="founders-page-progress-bar">
          <div className="founders-page-progress-fill" style={{ width: `${progressPercent}%` }} />
        </div>
        <div className="founders-hero-cta">
          <button type="button" className="btn btn-primary" onClick={handleCreateRepository}>
            Create new repository
          </button>
          <Link to="/repositories" className="btn btn-secondary">
            Browse repositories
          </Link>
        </div>
      </section>

      <section className="founders-page-info">
        <h3 className="welcome-where-founders-heading">Founders Program</h3>
        <p className="founders-page-info-lead">
          Founders are the first 100 contributors who help seed the archive with real electronics
          repair documentation.
        </p>
        <div className="founders-recognition-highlight">
          <h3 className="founders-lifetime-benefits-heading">
            Lifetime Benefits
            <span className="founders-lifetime-benefits-divider" aria-hidden="true">
              ·
            </span>
            <span className="founders-lifetime-benefits-note">One approved contribution can qualify</span>
          </h3>
          <p className="founders-recognition-subtitle">
            Founding contributors receive visible, permanent reputation signals across the platform.
          </p>
          <div className="founders-benefits-badge-demo">
            <p className="founders-benefits-badge-demo-caption">Example next to your name on profiles and posts</p>
            <div className="founders-benefits-badge-demo-row">
              <span className="founders-benefits-badge-demo-name">username</span>
              <span className="founders-benefits-badge-demo-badges">
                <CertifiedBadge
                  user={{ is_founding_contributor: true, forum_certified: true }}
                  title="Founding contributor and verified (example)"
                />
              </span>
            </div>
          </div>
          <div className="founders-recognition-grid">
            <div className="founders-recognition-card">
              <h4>Wall Placement</h4>
              <p>Your profile is listed on the Wall of Founders for the launch cohort.</p>
            </div>
            <div className="founders-recognition-card">
              <h4>Founding Profile Badge</h4>
              <p>A founder badge appears on your public profile to mark early contribution.</p>
            </div>
            <div className="founders-recognition-card">
              <h4>Automatic Verified Repos Status</h4>
              <p>
                Your repositories receive verified status automatically as part of founder recognition.{' '}
                <Link to="/docs#verified-users" className="founders-recognition-doc-link">
                  More about verification
                </Link>
              </p>
            </div>
          </div>
          <div className="founders-qualifications">
            <h4>Doing one of the following will activate your founding member status</h4>
            <ul>
              <li>New commit</li>
              <li>Circuit notes</li>
              <li>New repository</li>
              <li>New issue</li>
              <li>New release</li>
              <li>Correction</li>
            </ul>
          </div>
        </div>
        <p className="founders-page-info-note">
          Legal, provenance, and moderation policy remain in <Link to="/docs">Docs</Link>.
        </p>
      </section>

      <section className="founders-page-wall">
        <div className="founders-page-controls">
          <div>
            <h2>Founding Engineers</h2>
            <p>{total} founders listed</p>
          </div>
          <div className="founders-sort-wrap">
            <label htmlFor="founders-page-sort">Sort</label>
            <select
              id="founders-page-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="most_contributions">Most contributions</option>
              <option value="newest_founders">Newest founders</option>
            </select>
          </div>
        </div>

        {loading ? (
          <p className="founders-page-empty">Loading founders...</p>
        ) : founders.length === 0 ? (
          <p className="founders-page-empty">No founders have qualified yet.</p>
        ) : (
          <div className="founders-page-grid">
            {founders.map((founder) => {
              const cardBody = (
                <>
                  <div className="founders-page-avatar-wrap">
                    {founder.avatar_url ? (
                      <img
                        src={founder.avatar_url}
                        alt={`${founder.username} avatar`}
                        className="founders-page-avatar"
                      />
                    ) : (
                      <div className="founders-page-avatar founders-page-avatar-fallback">
                        {(founder.username || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="founders-page-meta">
                    <p className="founders-page-username">{founder.username}</p>
                    <CertifiedBadge
                      user={{
                        is_founding_contributor: true,
                      }}
                    />
                    <p className="founders-page-contribs">{founder.approved_contributions} approved contributions</p>
                  </div>
                </>
              )
              if (!isAuthenticated) {
                return (
                  <div key={founder.user_id} className="founders-page-card">
                    {cardBody}
                  </div>
                )
              }
              return (
                <Link key={founder.user_id} to={`/users/${founder.user_id}`} className="founders-page-card">
                  {cardBody}
                </Link>
              )
            })}
          </div>
        )}

        {!loading && totalPages > 1 && (
          <div className="founders-page-pagination">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </section>
    </div>
  )
}

export default FoundersWall
